// ChangePasswordPage.jsx
// Shown on first login when must_change_password = true.
// Forces the employee to set a new personal password before entering the app.

import React, { useState, useMemo } from 'react';
import { KeyRound, Loader, CheckCircle, AlertCircle, Eye, EyeOff, Check, X } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

// ── Password rules ────────────────────────────────────────────────────────────
const RULES = [
  { id: 'length',  label: 'At least 8 characters',          test: (p) => p.length >= 8 },
  { id: 'upper',   label: 'At least one uppercase letter',   test: (p) => /[A-Z]/.test(p) },
  { id: 'number',  label: 'At least one number',             test: (p) => /[0-9]/.test(p) },
  { id: 'symbol',  label: 'At least one symbol (!@#$…)',     test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordRules({ password, confirm }) {
  // eslint-disable-next-line no-unused-vars
  const _allPassing = RULES.every((r) => r.test(password));
  const matchPassing = password.length > 0 && password === confirm;

  if (!password) return null;

  return (
    <div className="space-y-1.5 py-1">
      {RULES.map((rule) => {
        const ok = rule.test(password);
        return (
          <div key={rule.id} className={`flex items-center gap-2 text-xs transition-colors ${ok ? 'text-green-600' : 'text-gray-400'}`}>
            {ok
              ? <Check size={13} className="flex-shrink-0 text-green-500" />
              : <X size={13} className="flex-shrink-0 text-gray-300" />
            }
            {rule.label}
          </div>
        );
      })}
      {confirm.length > 0 && (
        <div className={`flex items-center gap-2 text-xs transition-colors ${matchPassing ? 'text-green-600' : 'text-red-400'}`}>
          {matchPassing
            ? <Check size={13} className="flex-shrink-0 text-green-500" />
            : <X size={13} className="flex-shrink-0 text-red-400" />
          }
          Passwords match
        </div>
      )}
    </div>
  );
}

export default function ChangePasswordPage() {
  const { userProfile, logout, markPasswordChanged } = useAuth();
  const [pw,          setPw]          = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPw,      setShowPw]      = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [done,        setDone]        = useState(false);

  const allRulesPassing = useMemo(() => RULES.every((r) => r.test(pw)), [pw]);
  const canSubmit = allRulesPassing && pw === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesPassing) { setError('Please satisfy all password requirements.'); return; }
    if (pw !== confirm)   { setError('Passwords do not match.'); return; }

    setSaving(true);
    try {
      // 1. Clear DB flag FIRST while the current session is still valid.
      //    This must complete before updateUser() fires the USER_UPDATED auth
      //    event — otherwise onAuthStateChange re-fetches the profile and sees
      //    must_change_password: true, overwriting our local patch.
      const { error: dbErr } = await supabase
        .from('users')
        .update({ must_change_password: false })
        .eq('id', userProfile.id);
      if (dbErr) throw new Error('Could not clear password flag: ' + dbErr.message);

      // 2. Patch local state so the UI unblocks immediately.
      markPasswordChanged();

      // 3. Update the Supabase auth password.
      //    USER_UPDATED event fires here — profile re-fetch will now see false.
      const { error: pwErr } = await supabase.auth.updateUser({ password: pw });
      if (pwErr) throw pwErr;

      setDone(true);
      setTimeout(() => { window.location.hash = '/'; window.location.reload(); }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update password. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="DocketFlow" className="mx-auto h-28 w-auto drop-shadow-xl mb-3" />
          <p className="text-blue-100 text-sm">Manage Cases. Empower Justice.</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">

          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <KeyRound size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Change your password</h2>
              <p className="text-xs text-gray-500">
                You're using a temporary password. Please set a new one to continue.
              </p>
            </div>
          </div>

          {done ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
              <p className="text-green-700 text-sm font-medium">
                Password updated! Taking you to the app…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Employee ID badge */}
              {userProfile?.employee_id && (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm">
                  <span className="text-slate-500">Your Employee ID</span>
                  <span className="font-mono font-semibold text-indigo-600">{userProfile.employee_id}</span>
                </div>
              )}

              {/* New password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Min. 8 characters"
                    autoFocus
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Live password rules */}
              <PasswordRules password={pw} confirm={confirm} />

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex gap-2">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={saving || !canSubmit}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
              >
                {saving && <Loader size={16} className="animate-spin" />}
                Set new password & continue
              </button>

              <button
                type="button"
                onClick={logout}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-1 transition"
              >
                Sign out
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-blue-100 text-sm mt-6">© Phoenix Tech · DocketFlow</p>
      </div>
    </div>
  );
}
